import React, { useState, useEffect } from 'react';
import { Menu, ShoppingBag, Search, X, Tag, ChevronDown } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const IDEALAGE_LOGO = 'https://customer-assets.emergentagent.com/job_902719ec-3f1d-441e-9a91-2c280a563b23/artifacts/ie24qqvd_White-Idealage-Logo-768x241.png';

const Header = ({ cartCount = 0, onCartClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [protocolsOpen, setProtocolsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const collections = [
    { name: 'Perimenopause', href: '#perimenopause', color: '#EC407A' },
    { name: 'Fitness & Metabolism', href: '#fitness', color: '#E57373' },
    { name: 'Anti-Aging', href: '#anti-aging', color: '#9575CD' },
    { name: 'Mind & Mood', href: '#mind-mood', color: '#64B5F6' },
    { name: 'Immune Support', href: '#immune-support', color: '#81C784' },
    { name: 'Digestive & Blood Sugar', href: '#digestive-blood-sugar', color: '#FFB74D' },
    { name: 'Body Recomp Phase 1', href: '#body-recomp-phase-1', color: '#26A69A' },
    { name: 'Body Recomp Phase 2', href: '#body-recomp-phase-2', color: '#26A69A' },
  ];

  const scrollToSection = (e, targetId) => {
    e.preventDefault();
    if (!onLanding) {
      // Navigate to landing page first, then scroll
      navigate(`/#${targetId}`);
      setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      const element = document.getElementById(targetId);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const goToSale = (e) => {
    e.preventDefault();
    navigate('/memorial-day-sale');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  const goToJuly4Sale = (e) => {
    e.preventDefault();
    navigate('/july-4-sale');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  const goToFiber = (e) => {
    e.preventDefault();
    navigate('/fiber');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  const goToLaborDaySale = (e) => {
    e.preventDefault();
    navigate('/labor-day-sale');
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0 });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex flex-col items-start shrink-0">
            <img 
              src={IDEALAGE_LOGO} 
              alt="Ideal Age" 
              className={`h-8 w-auto max-w-none transition-all duration-300 ${
                isScrolled ? 'brightness-0' : 'brightness-100'
              }`}
            />
            <span className={`text-[10px] tracking-wide mt-1 transition-colors duration-300 ${
              isScrolled ? 'text-stone-500' : 'text-white/70'
            }`}>
              Powered by MD Logic Health®
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            <div className="relative">
              <button
                onClick={() => setProtocolsOpen((v) => !v)}
                data-testid="protocols-dropdown-trigger"
                className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-all duration-300 hover:opacity-70 ${
                  isScrolled ? 'text-stone-700' : 'text-white/90'
                }`}
              >
                Protocols
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${protocolsOpen ? 'rotate-180' : ''}`} />
              </button>
              {protocolsOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setProtocolsOpen(false)} />
                  <div
                    data-testid="protocols-dropdown-menu"
                    className="absolute left-0 top-full mt-3 z-50 w-64 bg-white rounded-xl shadow-xl border border-stone-100 p-2"
                  >
                    {collections.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        data-testid={`protocol-dropdown-${item.href.replace('#', '')}`}
                        onClick={(e) => {
                          scrollToSection(e, item.href.replace('#', ''));
                          setProtocolsOpen(false);
                        }}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 text-sm text-stone-700 transition-colors"
                      >
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                        {item.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
            <a
              href="/fiber"
              onClick={goToFiber}
              data-testid="fiber-nav"
              className={`text-sm font-medium tracking-wide transition-all duration-300 hover:opacity-70 ${
                isScrolled ? 'text-emerald-700' : 'text-lime-200'
              }`}
            >
              Daily Multi-Fiber
            </a>
            {/* Labor Day Sale nav (unpublished) — uncomment to re-enable:
            <a
              href="/labor-day-sale"
              onClick={goToLaborDaySale}
              data-testid="labor-day-sale-nav"
              className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide px-3 py-1.5 rounded-full bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm whitespace-nowrap"
            >
              <Tag className="w-3.5 h-3.5" />
              Labor Day Sale
            </a> */}
            {/* July 4th Sale nav (unpublished) — uncomment to re-enable:
            <a
              href="/july-4-sale"
              onClick={goToJuly4Sale}
              data-testid="july-4-sale-nav"
              className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide px-3 py-1.5 rounded-full bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm"
            >
              <Tag className="w-3.5 h-3.5" />
              July 4th Sale
            </a> */}
            {/* Memorial Day Sale nav (unpublished) — uncomment to re-enable:
            <a
              href="/memorial-day-sale"
              onClick={goToSale}
              data-testid="memorial-day-sale-nav"
              className="inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide px-3 py-1.5 rounded-full bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm"
            >
              <Tag className="w-3.5 h-3.5" />
              Memorial Day Sale
            </a> */}
          </nav>

          <div className="flex items-center gap-4">
            <button 
              className={`p-2 rounded-full transition-colors ${
                isScrolled ? 'hover:bg-stone-100 text-stone-700' : 'hover:bg-white/10 text-white'
              }`}
            >
              <Search className="w-5 h-5" />
            </button>
            
            <button 
              onClick={onCartClick}
              className={`relative p-2 rounded-full transition-colors ${
                isScrolled ? 'hover:bg-stone-100 text-stone-700' : 'hover:bg-white/10 text-white'
              }`}
            >
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-600 text-white text-xs rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            <button 
              onClick={() => setMobileMenuOpen(true)}
              className={`lg:hidden p-2 rounded-full transition-colors ${
                isScrolled ? 'hover:bg-stone-100 text-stone-700' : 'hover:bg-white/10 text-white'
              }`}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60]">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          <div className="absolute right-0 top-0 h-full w-[300px] bg-white shadow-2xl">
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-stone-100 flex items-center justify-between">
                <div className="flex flex-col items-start shrink-0">
                  <img 
                    src={IDEALAGE_LOGO} 
                    alt="Ideal Age" 
                    className="h-6 w-auto max-w-none brightness-0"
                  />
                  <span className="text-[9px] text-stone-500 mt-1">
                    Powered by MD Logic Health®
                  </span>
                </div>
                <button 
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-stone-600" />
                </button>
              </div>
              <nav className="flex-1 p-6">
                {/* Labor Day Sale nav (unpublished) — uncomment to re-enable:
                <a
                  href="/labor-day-sale"
                  onClick={goToLaborDaySale}
                  data-testid="labor-day-sale-nav-mobile"
                  className="flex items-center gap-2 mb-6 px-4 py-3 rounded-full bg-rose-600 text-white text-sm font-semibold justify-center hover:bg-rose-700 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  Labor Day Sale
                </a> */}
                {/* July 4th Sale nav (unpublished) — uncomment to re-enable:
                <a
                  href="/july-4-sale"
                  onClick={goToJuly4Sale}
                  data-testid="july-4-sale-nav-mobile"
                  className="flex items-center gap-2 mb-6 px-4 py-3 rounded-full bg-rose-600 text-white text-sm font-semibold justify-center hover:bg-rose-700 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  July 4th Sale
                </a> */}
                {/* Memorial Day Sale nav (unpublished) — uncomment to re-enable:
                <a
                  href="/memorial-day-sale"
                  onClick={goToSale}
                  data-testid="memorial-day-sale-nav-mobile"
                  className="flex items-center gap-2 mb-6 px-4 py-3 rounded-full bg-rose-600 text-white text-sm font-semibold justify-center hover:bg-rose-700 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  Memorial Day Sale
                </a> */}
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-4">Protocols</p>
                {collections.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href.replace('#', ''))}
                    className="flex items-center gap-3 py-3 text-lg text-stone-700 hover:text-amber-700 transition-colors"
                  >
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    {item.name}
                  </a>
                ))}
                <a
                  href="/fiber"
                  onClick={goToFiber}
                  data-testid="fiber-nav-mobile"
                  className="flex items-center gap-2 py-3 text-lg font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">New</span>
                  Daily Multi-Fiber
                </a>
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
